import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { z } from 'zod';

import { NOTIFICATION_TYPE_KEYS } from '@/features/notifications/copy';
import {
    NotificationsPage,
    type NotificationsSearch,
} from '@/features/notifications/NotificationsPage';

/*
  The notifications page (ALS-71). Its tab (`category`), user, type, View as and page ride the URL, the way the
  registers' filters do (ADR 0031), so a filtered view can be shared and a reload restores it. The
  prototype's one URL-only state, filtered-empty, is then any link whose filters match nothing.

  `.catch(undefined)` keeps a junk value in a hand-edited URL from throwing the route: an unknown
  type or tab falls back to All rather than an error page.
*/
const notificationsSearch = z.object({
    category: z.enum(['Orders', 'Payments', 'Returns']).optional().catch(undefined),
    user: z.coerce.number().int().min(1).optional().catch(undefined),
    type: z
        .string()
        .refine((key) => NOTIFICATION_TYPE_KEYS.includes(key))
        .optional()
        .catch(undefined),
    viewAs: z.enum(['Assignee', 'Rm']).optional().catch(undefined),
    page: z.coerce.number().int().min(1).optional().catch(undefined),
});

export const Route = createFileRoute('/_app/notifications')({
    validateSearch: (search) => notificationsSearch.parse(search),
    component: NotificationsRoute,
});

function NotificationsRoute() {
    const search = Route.useSearch();
    const navigate = useNavigate();
    return (
        <NotificationsPage
            search={search}
            onSearchChange={(next: NotificationsSearch) =>
                void navigate({
                    to: '/notifications',
                    search: next,
                    replace: true,
                    resetScroll: false,
                })
            }
        />
    );
}
